import Autocomplete from "@mui/material/Autocomplete";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { createFilterOptions } from "@mui/material/Autocomplete";
import type { FilterOptionsState } from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import debounce from "lodash/debounce";
import React, { Component, SyntheticEvent } from "react";
import { AppContext } from "../../common/AppContext";
import DropIcon from "../../resources/images/icons-chevron-double.svg";
import RemoveIcon from "../../resources/images/icons-remove.svg";
import type {
  AbstractFilterSelectProps,
  AbstractFilterSelectState,
  AutocompleteRenderOptionState,
  BaseSelectorOption,
  SelectorDataList
} from "../../types/components/selector.types";
import { TextField } from "../TextField";

export class AbstractFilterSelect<
  TOption extends BaseSelectorOption = BaseSelectorOption
> extends Component<AbstractFilterSelectProps<TOption>, AbstractFilterSelectState<TOption>> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    listType: "Filter",
    maxResults: 50,
    initialSearch: false,
    multiple: true,
    clearable: true,
    hasNoneItem: false,
    hasAllItem: false,
    hasNewItem: false,
    getItemId: undefined,
    debounce: true,
    clearOnSelection: false,
    clearResultsOnSelection: false,
    excludeItems: [],
    matchFrom: "any"
  };

  initialItems: TOption[] | undefined;
  value: TOption | string | number | null;
  hasTyped: boolean;
  filterOptions: (options: TOption[], state: FilterOptionsState<TOption>) => TOption[];
  debounceLoadItems: ReturnType<typeof debounce>;
  ignorePendingResults: boolean;
  dataList: SelectorDataList<TOption> | undefined;

  constructor(props: AbstractFilterSelectProps<TOption>) {
    super(props);

    const allItems = this.filterItems(props.items || props.initialItems || []);

    this.state = {
      selectedValues: props.value ? this.findValue(allItems, props.value) : null,
      allValues: allItems,
      valid: null,
      forceBlur: false
    };

    this.initialItems = props.initialItems;
    this.value = props.value ?? null;
    this.hasTyped = false;

    this.filterOptions = createFilterOptions({
      matchFrom: props.matchFrom,
      // limit: props.maxResults,
      ignoreCase: true,
      stringify: (option) => this.getItemLabel(option)
    });

    this.debounceLoadItems = debounce((s) => this.loadItems(s), 500);
    this.ignorePendingResults = false;
  }

  onChange(e: SyntheticEvent, selected: TOption | TOption[] | null): void {
    const { onChange, clearOnSelection, clearResultsOnSelection, validationMethod } = this.props;
    const { valid } = this.state;

    if (validationMethod) {
      const target = e.target as HTMLInputElement;
      const newValid = validationMethod(e, target.value);
      if (newValid !== valid) this.setState({ valid: newValid });
    }

    if (clearOnSelection) {
      const newState: Partial<AbstractFilterSelectState<TOption>> = {
        selectedValues: null,
        forceBlur: true
      };
      if (clearResultsOnSelection) {
        newState.allValues = [];
      }
      this.setState(newState as AbstractFilterSelectState<TOption>, () => {
        if (onChange) onChange(e, selected);
      });
    } else {
      this.setState({ selectedValues: selected }, () => {
        if (onChange) onChange(e, selected);
      });
    }
  }

  getItemLabel(item: TOption): string {
    const {
      getItemLabel,
      hasNoneItem,
      noneItemLabel,
      hasAllItem,
      allItemLabel,
      hasNewItem,
      newItemLabel,
      translate
    } = this.props;
    if (getItemLabel) return getItemLabel(item);

    if (hasNoneItem && item.isNoneItem === true)
      return noneItemLabel || (translate ? translate("common.none") : "None");
    else if (hasAllItem && item.isAllItem === true)
      return allItemLabel || (translate ? translate("common.all") : "All");
    else if (hasNewItem && item.isNewItem === true)
      return newItemLabel || (translate ? translate("common.create") : "New");
    return item.label || item.name || "";
  }

  getItemSecondaryLabel(_item: TOption): string | null {
    return null;
  }

  getItemState(item: TOption): string | null {
    const { getItemState } = this.props;
    return getItemState ? getItemState(item) : null;
  }

  getItemSecondaryAction(_item: TOption): React.ReactNode | null {
    return null;
  }

  getList(_dataList: unknown): TOption[] {
    return [];
  }

  compareItems(a: TOption, b: TOption): boolean {
    const { compareItems } = this.props;
    if (compareItems) return compareItems(a, b);
    return a === b;
  }

  onInputChange(e: SyntheticEvent | null, value: string | null, reason: string): void {
    const { debounce: useDebounce, onInputChange } = this.props;

    if (value === null) {
      this.value = null;
      if (onInputChange) onInputChange("");
    } else if (value === "" || reason === "reset") {
      this.clearResults();
      if (onInputChange) onInputChange("");
      return;
    }
    if (e && reason === "input" && value !== null) {
      this.hasTyped = true;
      this.ignorePendingResults = false;
      if (onInputChange) onInputChange(value);
      if (useDebounce) this.debounceLoadItems(value);
      else this.loadItems(value);
    }
  }

  clearResults(): void {
    const { items } = this.props;
    this.ignorePendingResults = true;
    this.value = "";
    this.setItems(this.initialItems ? this.initialItems : items ? items : []);
  }

  onBlur(e: React.FocusEvent<HTMLElement>): void {
    const { onBlur, validationMethod } = this.props;
    const { valid, forceBlur } = this.state;

    if (validationMethod) {
      const target = e.target as HTMLInputElement;
      const newValid = validationMethod(e, target.value);
      if (newValid !== valid) this.setState({ valid: newValid });
    }

    if (forceBlur) this.setState({ forceBlur: false });

    if (onBlur) onBlur(e);
  }

  componentDidMount(): void {
    if (this.props.initialSearch) this.loadItems(null, true);
  }

  componentDidUpdate(
    prevProps: AbstractFilterSelectProps<TOption>,
    _prevState: AbstractFilterSelectState<TOption>
  ): void {
    const { allValues } = this.state;

    if (prevProps.initialItems !== this.props.initialItems) {
      this.initialItems = this.props.initialItems;
      if (this.props.initialItems) this.setItems(this.props.initialItems);
    }
    if (prevProps.items !== this.props.items) {
      if (this.props.items) this.setItems(this.props.items);
    }
    if (prevProps.value !== this.props.value) {
      this.value = this.props.value ?? null;
      this.setState({ selectedValues: this.findValue(allValues, this.value) });
    }
  }

  findValue(
    items: TOption[],
    value: TOption | string | number | null | undefined
  ): TOption | TOption[] | null {
    if (value && typeof value === "object") return value as TOption;
    return this.findValueItem(items, value);
  }

  findValueItem(
    _items: TOption[],
    _value: TOption | string | number | null | undefined
  ): TOption | null {
    return null;
  }

  setItems(allValues: TOption[] | undefined): void {
    const filteredItems = this.filterItems(allValues || []);
    this.setState({
      allValues: filteredItems,
      selectedValues: this.findValue(filteredItems, this.props.value)
    });
  }

  filterItems(allValues: TOption[]): TOption[] {
    const { filterItems } = this.props;
    if (filterItems) return filterItems(allValues);
    return allValues;
  }

  loadItems(value: string | null, initialSearch?: boolean): void {
    const { callApi, listType } = this.props;

    if (callApi === false && initialSearch !== true) return;

    this.value = value;
    if (initialSearch !== true && (!value || value.length === 0)) return;

    this.context?.setLoading?.("search" + listType, true);

    this.getData(value)
      .then((list: unknown) => {
        if (this.ignorePendingResults) this.ignorePendingResults = false;
        else {
          const values = this.getList(list);
          this.setItems(values);
        }
      })
      .catch((_error: unknown) => {
        // Error handling - could be enhanced
      })
      .finally(() => this.context?.setLoading?.("search" + listType, false));
  }

  getData(value: string | null): Promise<unknown> {
    const { getData } = this.props;
    const customGetData = getData ? getData(value) : null;
    return customGetData
      ? customGetData
      : (this.dataList?.get(this.context, value) ?? Promise.resolve([]));
  }

  renderOption(
    props: React.HTMLAttributes<HTMLLIElement>,
    option: TOption,
    state: AutocompleteRenderOptionState
  ): React.ReactNode {
    const { getItemId } = this.props;

    const id = getItemId ? getItemId(option) : option.id;
    const name = this.getItemLabel(option);
    const itemState = this.getItemState(option);
    const secondaryLabel = this.getItemSecondaryLabel(option);
    const secondaryAction = this.getItemSecondaryAction(option);
    const matches = match(name, state.inputValue, {
      insideWords: true,
      requireMatchAll: false
    });
    const parts = parse(name, matches);

    return (
      <ListItem
        {...props}
        className={
          "klayo-filterselector_option" +
          (itemState ? " klayo-filterselector_option--" + itemState : "")
        }
        key={id}
        secondaryAction={secondaryAction ?? undefined}
      >
        <ListItemText
          className='klayo-filterselector_option_text'
          primary={
            parts.length === 1 ? (
              name
            ) : (
              <div>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    className={
                      "klayo-selector_itempart" +
                      (part.highlight === true ? " klayo-selector_highlight" : "")
                    }
                  >
                    {part.text}
                  </span>
                ))}
              </div>
            )
          }
          secondary={secondaryLabel ?? undefined}
        />
      </ListItem>
    );
  }

  isOptionEqualToValue(a: TOption | null, b: TOption | null): boolean {
    if (a === null || b === null) return false;

    const { getItemId } = this.props;
    if (!getItemId) return a === b;

    return getItemId(a) === getItemId(b);
  }

  getItemId(item: TOption): string | number {
    const { getItemId } = this.props;
    return getItemId ? getItemId(item) : item.id || "";
  }

  render(): React.ReactNode {
    const {
      label,
      sx,
      placeholder,
      focusPlaceholder,
      multiple,
      clearable,
      validationText,
      helperText,
      disabled,
      loading,
      hasNoneItem,
      noneItemLabel,
      hasAllItem,
      allItemLabel,
      hasNewItem,
      noOptionsText,
      disabledSearch,
      filterOptionsProps,
      translate
    } = this.props;

    // Use translated values if translate function is available
    const effectiveNoOptionsText =
      noOptionsText || translate?.("common.noSearchResults") || "No search results";
    const effectiveNoneItemLabel = noneItemLabel || translate?.("common.none") || "None";
    const effectiveAllItemLabel = allItemLabel || translate?.("common.all") || "All";
    const { allValues, selectedValues, valid, forceBlur } = this.state;

    const options: TOption[] = [];
    if (hasNoneItem)
      options.push({ id: -10, name: effectiveNoneItemLabel, isNoneItem: true } as TOption);
    if (hasAllItem)
      options.push({ id: -11, name: effectiveAllItemLabel, isAllItem: true } as TOption);
    if (hasNewItem) options.push({ id: -12, name: "", isNewItem: true } as TOption);

    if (allValues && Array.isArray(allValues) && allValues.length !== 0) options.push(...allValues);
    else if (this.initialItems && Array.isArray(this.initialItems))
      options.push(...this.initialItems);

    return (
      <Autocomplete<TOption, boolean, boolean, false>
        className='klayo-filterselector klayo-selector'
        options={options}
        value={selectedValues}
        getOptionLabel={(option) => this.getItemLabel(option)}
        filterOptions={filterOptionsProps || this.filterOptions}
        isOptionEqualToValue={(option, value) => this.isOptionEqualToValue(option, value)}
        autoComplete
        includeInputInList
        disableClearable={clearable === false}
        filterSelectedOptions
        popupIcon={<DropIcon />}
        clearIcon={<RemoveIcon />}
        onBlur={this.onBlur.bind(this)}
        handleHomeEndKeys
        noOptionsText={effectiveNoOptionsText}
        ChipProps={{
          deleteIcon: <RemoveIcon />
        }}
        multiple={multiple}
        renderOption={(props, option, state) =>
          this.renderOption(props, option, state as AutocompleteRenderOptionState)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            disabled={disabled}
            loading={loading}
            placeholder={placeholder || translate?.("common.search") || "Search"}
            focusPlaceholder={focusPlaceholder}
            error={valid === false}
            helperText={valid === false ? validationText : helperText}
            forceBlur={forceBlur}
            sx={{ width: "100%" }}
            endAdornment={params.InputProps.endAdornment}
          />
        )}
        onChange={(event, value) => this.onChange(event, value as TOption | TOption[] | null)}
        onInputChange={(event, value, reason) => this.onInputChange(event, value, reason)}
        sx={sx}
        onKeyDown={(e) => {
          if (disabledSearch) e.preventDefault();
        }}
      />
    );
  }
}
